import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useReducedMotion, type MotionValue } from 'framer-motion';
import { useCopy } from './copy/copyMode';
import { SlackPanel } from './SlackIntegrationGraph';
// The messaging platforms Giga lives in — Slack is live today; Teams, iMessage
// and WhatsApp are on the way. Each renders as its real brand icon (simpleicons,
// with a dashboard-icons fallback) so the row reads instantly as "works where
// you already chat." An optional `iconUrl` overrides the default simpleicons
// source for brands whose simpleicons slug is unreliable (e.g. Teams).
type Platform = {
  slug: string;
  name: string;
  status: 'live' | 'coming';
  iconUrl?: string;
};
const PLATFORMS: Platform[] = [{
  slug: 'slack',
  name: 'Slack',
  status: 'live'
}, {
  slug: 'microsoftteams',
  name: 'Teams',
  status: 'coming',
  // simpleicons dropped the reliable microsoftteams slug — use the colored
  // Teams icon from the dashboard-icons CDN directly so it always renders.
  iconUrl: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg'
}, {
  slug: 'imessage',
  name: 'iMessage',
  status: 'coming'
}, {
  slug: 'whatsapp',
  name: 'WhatsApp',
  status: 'coming'
}];
function PlatformPill({
  platform
}: {
  platform: Platform;
}) {
  const isComing = platform.status === 'coming';
  return <div className={`inline-flex items-center gap-2 rounded-full border border-neutral-200/70 bg-white/80 px-3.5 py-2 shadow-sm backdrop-blur-sm ${isComing ? 'opacity-70' : ''}`} data-id="element-4264" data-mp="1434dgj60dy5f">
      <img src={platform.iconUrl || `https://cdn.simpleicons.org/${platform.slug}`} alt="" className={`h-4 w-4 object-contain ${isComing ? 'grayscale' : ''}`} loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${platform.slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-4265" data-mp="v63w2whcx0bh" />

      <span className="text-sm font-semibold text-[#2B211A]" data-id="element-4266" data-mp="2e51qhbjk3nyp">
        {platform.name}
      </span>
      {isComing ? <span className="text-[11px] font-medium text-neutral-400" data-id="element-4267" data-mp="2932pg8bah34x">Soon</span> : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0F9D58]" data-id="element-4268" data-mp="2462g9oj57k9l">
          <span className="relative flex h-1.5 w-1.5" data-id="element-4269" data-mp="2050pzx77eijr">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#0F9D58] opacity-75" data-id="element-4270" data-mp="2wtyit9ve86ea" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#0F9D58]" data-id="element-4271" data-mp="2rudd8glpwzsl" />
          </span>
          Live
        </span>}
    </div>;
}
export function LivesInSlack({
  progress
}: {
  progress?: MotionValue<number>;
}) {
  const copy = useCopy();
  const containerRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const internalScroll = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const scrollYProgress = progress || internalScroll.scrollYProgress;
  // Scroll-linked scale: the panel starts noticeably smaller and visibly
  // expands to full size as the section scrolls up into view, giving the beat
  // a bit of interactivity. It's anchored to the top so it only ever grows
  // downward (never rising over the headline). useTransform clamps outside the
  // input range, so it holds small before it enters and settles at full size
  // once the panel is comfortably in frame.
  const rawScale = useTransform(scrollYProgress, [0.08, 0.5], [0.8, 1.0]);
  const panelScale = useSpring(rawScale, {
    stiffness: 120,
    damping: 24,
    mass: 0.4
  });
  return <section className={`relative w-full overflow-hidden bg-[#F4F0EA] text-[#2B211A] ${progress ? 'h-screen flex flex-col justify-start pt-24 md:pt-28' : 'h-full pt-24 md:pt-28 pb-8 md:pb-20'}`} data-id="element-4272" data-mp="1q0kaj4xmz3gh">
      {/* Decorative grain texture. Was a 3.2 MB autoplay video, which the
        browser downloaded on every load (mobile included) for a background
        effect the user barely notices. Swapped for a ~10 KB static WebP
        grain — the top of the section is washed to near-white by the
        overlay below anyway, so the texture only reads faintly. */}
      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Untitled_design_(6)_1.webp" alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-4273" data-mp="202w50ukglmpx" />
      <div aria-hidden="true" className="absolute inset-0 bg-[#F4F0EA]/60" data-id="element-4274" data-mp="12f43xpia61d2" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 flex flex-col h-full" data-id="element-4275" data-mp="12h8id1dt2irl">
        <div className="text-center mb-6 md:mb-8 shrink-0" data-id="element-4276" data-mp="1h9s890by2tr3">
          {/* Two deliberate lines at every width — the mention on top, what it
           applies to beneath — so the headline always reads as a stacked pair
           rather than wrapping wherever the container happens to end. */}
          <h2 className="text-4xl md:text-4xl lg:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#2B211A]" data-id="element-4277" data-mp="2nujmymzqtng2">
            Just @mention Giga in
            <br data-id="element-4278" data-mp="30bm072m2y428" />
            any conversation
          </h2>
          <p className="mt-5 md:mt-6 max-w-2xl mx-auto text-base md:text-lg text-neutral-500 font-medium leading-relaxed" data-id="element-4279" data-mp="2mbbai2wiavin">
            It pulls answers from every system that's connected.
          </p>
        </div>

        {/* The live Slack / Teams panel: toggles between platforms and hands
         back a generated PDF deliverable inline. Anchored to the top so the
         scroll-scale only ever grows it downward. */}
        <div ref={containerRef} className="relative z-10 mx-auto flex w-full max-w-[720px] flex-1 items-start justify-center" data-id="element-4280" data-mp="358un1gi6aw9t">
          <motion.div style={{
          scale: reducedMotion ? 1 : panelScale,
          transformOrigin: 'center top'
        }} className="w-full" data-id="element-4281" data-mp="399oak8rpe926">
            <SlackPanel data-id="element-4282" data-mp="5mxqc9tjal50" />
          </motion.div>
        </div>

        {/* Platform availability — the messaging apps Giga plugs into, with a
         live/coming status on each, so it's clear where Giga works today and
         what's next. */}
        <div className="relative z-10 mt-8 md:mt-10 shrink-0 flex flex-wrap items-center justify-center gap-2.5 md:gap-3" data-id="element-4283" data-mp="bhkomb5w7e02">
          {PLATFORMS.map(platform => <PlatformPill key={platform.name} platform={platform} data-id="element-4284" data-mp="1j4wc4v4pyoz" />)}
        </div>
      </div>
    </section>;
}